import { useEffect, useRef, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
    Tooltip, Legend, ResponsiveContainer, Cell,
} from 'recharts';
import { X, CheckCircle2, AlertCircle } from 'lucide-react';
import {
    getBroadcastSummary,
    getBroadcastRevenue,
    getBroadcastGroupDetail,
    BroadcastResult,
    DomainHealth,
} from '@/services/api/meta-analytics';
import {
    fmtMoney, fmtN, fmtPct, fmtDate,
    TZ_LABEL, STATUS_LABEL, STATUS_COLOR,
} from './utils';

// ── helpers ───────────────────────────────────────────────────────────────────

function Skeleton({ className = '' }: { className?: string }) {
    return <div className={`animate-pulse bg-gray-100 rounded ${className}`} />;
}

const DOMAIN_COLORS = ['#6366f1', '#10b981', '#f59e0b', '#3b82f6', '#ec4899'];

// ── RevenueTooltip ────────────────────────────────────────────────────────────

function RevenueTooltip({ active, payload, label }: {
    active?: boolean;
    payload?: { name: string; value: number; color: string }[];
    label?: string;
}) {
    if (!active || !payload?.length) return null;
    return (
        <div className="rounded-xl border border-gray-200 bg-white/95 px-3 py-2 shadow-lg text-sm">
            <p className="text-xs text-gray-400 mb-1">{label}</p>
            {payload.map((p, i) => (
                <div key={i} className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ background: p.color }} />
                    <span className="text-gray-600">{p.name}:</span>
                    <span className="font-semibold text-gray-900">{fmtMoney(p.value)}</span>
                </div>
            ))}
        </div>
    );
}

// ── Props ─────────────────────────────────────────────────────────────────────

export interface GroupPanelProps {
    groupId: number;
    groupTitle: string;
    groupStatus?: string;
    start: string;
    end: string;
    domainHealthMap: Record<string, DomainHealth>;
    allGroups: BroadcastResult[];
    onClose: () => void;
    onOpenDomain: (domain: string) => void;
}

// ── Componente ────────────────────────────────────────────────────────────────

export function GroupPanel({
    groupId,
    groupTitle,
    groupStatus,
    start,
    end,
    domainHealthMap,
    onClose,
    onOpenDomain,
}: GroupPanelProps) {
    const panelRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [onClose]);

    // ── queries ───────────────────────────────────────────────────────────────

    const summaryQuery = useQuery({
        queryKey: ['broadcast-summary-v2', groupId],
        queryFn: () => getBroadcastSummary(groupId),
        staleTime: 1000 * 60 * 5,
        refetchOnWindowFocus: false,
    });

    const revenueQuery = useQuery({
        queryKey: ['broadcast-revenue-v2', groupId, start, end],
        queryFn: () => getBroadcastRevenue(groupId, start, end),
        staleTime: 1000 * 60 * 5,
        refetchOnWindowFocus: false,
        retry: 1,
    });

    const detailQuery = useQuery({
        queryKey: ['broadcast-detail-v2', groupId, start, end],
        queryFn: () => getBroadcastGroupDetail(groupId, start, end),
        staleTime: 1000 * 60 * 5,
        refetchOnWindowFocus: false,
    });

    const summary = summaryQuery.data?.data;
    const revenue = revenueQuery.data?.data;
    const detail = detailQuery.data?.data;

    const revenueLoading = revenueQuery.isLoading || revenueQuery.isFetching;
    const summaryLoading = summaryQuery.isLoading;

    // ── funil ─────────────────────────────────────────────────────────────────

    const sent = summary?.total_sent ?? 0;
    const delivered = summary?.total_delivered ?? 0;
    const clicks = summary?.total_clicks ?? 0;
    const ctr = summary?.ctr ?? 0;
    const totalRev = revenue?.total_revenue ?? 0;

    const pct = (val: number) =>
        sent > 0 ? fmtPct((val / sent) * 100, 2) : '—';

    // ── gráfico revenue por dia ───────────────────────────────────────────────

    const { chartData, chartDomains } = useMemo(() => {
        if (!revenue?.by_date?.length) return { chartData: [], chartDomains: [] };
        const dateMap: Record<string, Record<string, number>> = {};
        const domSet = new Set<string>();
        for (const row of revenue.by_date) {
            if (!dateMap[row.date]) dateMap[row.date] = {};
            dateMap[row.date][row.domain] = (dateMap[row.date][row.domain] ?? 0) + row.revenue;
            domSet.add(row.domain);
        }
        const data = Object.entries(dateMap)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([date, vals]) => ({ date_fmt: fmtDate(date), ...vals }));
        return { chartData: data, chartDomains: [...domSet] };
    }, [revenue]);

    // ── timezones ─────────────────────────────────────────────────────────────

    const topCountries = useMemo(() => {
        if (!detail?.country_clicks?.length) return [];
        return [...detail.country_clicks]
            .sort((a, b) => b.clicks - a.clicks)
            .slice(0, 10);
    }, [detail]);

    const _maxCountryClicks = topCountries[0]?.clicks ?? 1; void _maxCountryClicks;

    const statusCls = groupStatus ? (STATUS_COLOR[groupStatus] ?? 'bg-gray-100 text-gray-500') : '';

    // ── revByDomain para lista de domains ─────────────────────────────────────

    const revByDomain = useMemo(() => {
        const m: Record<string, number> = {};
        revenue?.by_domain.forEach(d => { m[d.domain] = d.revenue; });
        return m;
    }, [revenue]);

    return (
        <div
            className="fixed inset-0 z-50 flex justify-end bg-black/40"
            onClick={e => {
                if (panelRef.current && !panelRef.current.contains(e.target as Node)) onClose();
            }}
            role="dialog"
            aria-modal="true"
        >
            <div
                ref={panelRef}
                className="relative h-full w-full max-w-3xl bg-white shadow-2xl overflow-y-auto flex flex-col"
            >
                {/* Header */}
                <div className="sticky top-0 z-10 bg-white border-b border-gray-100 px-6 py-4">
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                                <h2 className="font-semibold text-gray-900 text-base truncate" title={groupTitle}>
                                    {groupTitle}
                                </h2>
                                <span className="text-xs text-gray-400 shrink-0">#{groupId}</span>
                                {groupStatus && (
                                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium shrink-0 ${statusCls}`}>
                                        {STATUS_LABEL[groupStatus] ?? groupStatus}
                                    </span>
                                )}
                            </div>
                            <p className="text-xs text-gray-400 mt-0.5">{start} → {end}</p>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 rounded-lg border border-gray-100 text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors shrink-0"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                <div className="flex-1 px-6 py-6 space-y-8">

                    {/* Seção 1 — Mini funil */}
                    <section>
                        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Funil</h3>
                        {summaryLoading ? (
                            <div className="grid grid-cols-4 gap-3">
                                {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-16" />)}
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                {[
                                    { label: 'Enviados', value: sent, pct: '100%', color: 'text-blue-600 bg-blue-50' },
                                    { label: 'Entregues', value: delivered, pct: pct(delivered), color: 'text-emerald-600 bg-emerald-50' },
                                    { label: 'Cliques', value: clicks, pct: `${fmtPct(ctr, 2)} CTR`, color: 'text-indigo-600 bg-indigo-50' },
                                    { label: 'Revenue', value: null, pct: null, color: 'text-green-600 bg-green-50', isRev: true },
                                ].map(step => (
                                    <div key={step.label} className={`rounded-xl p-3 ${step.color.split(' ')[1]}`}>
                                        <p className="text-xs text-gray-500 mb-1">{step.label}</p>
                                        {'isRev' in step && step.isRev ? (
                                            revenueLoading
                                                ? <Skeleton className="h-7 w-20" />
                                                : <p className="text-xl font-bold text-green-700">{fmtMoney(totalRev)}</p>
                                        ) : (
                                            <p className={`text-xl font-bold ${step.color.split(' ')[0]}`}>
                                                {fmtN(step.value ?? 0)}
                                            </p>
                                        )}
                                        {step.pct && (
                                            <p className="text-xs text-gray-400 mt-0.5">{step.pct}</p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>

                    {/* Seção 2 — Domains deste grupo */}
                    <section>
                        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                            Domains
                        </h3>
                        {summaryLoading ? (
                            <div className="space-y-2">
                                {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-12" />)}
                            </div>
                        ) : !summary?.pages_by_domain?.length ? (
                            <p className="text-sm text-gray-400">Sem dados de domains.</p>
                        ) : (
                            <div className="divide-y divide-gray-100 rounded-xl border border-gray-100 overflow-hidden">
                                {summary.pages_by_domain.map(dom => {
                                    const health = domainHealthMap[dom.domain];
                                    const hasRev = health?.has_revenue ?? false;
                                    const domRev = revByDomain[dom.domain];
                                    return (
                                        <button
                                            key={dom.domain}
                                            className="w-full flex items-center gap-3 px-4 py-3 bg-white hover:bg-gray-50 transition-colors text-left"
                                            onClick={() => onOpenDomain(dom.domain)}
                                        >
                                            {hasRev ? (
                                                <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                                            ) : (
                                                <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
                                            )}
                                            <span className={`text-sm font-medium flex-1 truncate ${hasRev ? 'text-gray-800' : 'text-gray-400'}`}>
                                                {dom.domain}
                                            </span>
                                            <span className="text-xs text-gray-400 shrink-0">
                                                {dom.pages}p · {fmtN(dom.sent)} env
                                            </span>
                                            {revenueLoading ? (
                                                <Skeleton className="h-4 w-16 shrink-0" />
                                            ) : domRev !== undefined ? (
                                                <span className="text-sm font-semibold text-emerald-700 shrink-0">
                                                    {fmtMoney(domRev)}
                                                </span>
                                            ) : (
                                                <span className="text-xs text-red-400 shrink-0">Sem revenue</span>
                                            )}
                                            <span className="text-xs text-gray-300 shrink-0">→</span>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </section>

                    {/* Seção 3 — Cliques por hora */}
                    <section>
                        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                            Cliques por hora
                        </h3>
                        {detailQuery.isLoading ? (
                            <Skeleton className="h-40" />
                        ) : (detail?.hourly_clicks ?? []).length === 0 ? (
                            <p className="text-sm text-gray-400">Sem dados de cliques por hora.</p>
                        ) : (
                            <div className="rounded-xl border border-gray-100 p-4">
                                <ResponsiveContainer width="100%" height={150}>
                                    <BarChart
                                        data={Array.from({ length: 24 }, (_, h) => {
                                            const found = detail!.hourly_clicks.find(d => d.hour === h);
                                            return { label: `${String(h).padStart(2,'0')}h`, clicks: found?.clicks ?? 0, unique_leads: found?.unique_leads ?? 0 };
                                        })}
                                        margin={{ top: 2, right: 4, left: 0, bottom: 0 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                        <XAxis dataKey="label" tick={{ fontSize: 9 }} interval={1} />
                                        <YAxis tick={{ fontSize: 10 }} width={36} tickFormatter={v => fmtN(v as number)} />
                                        <Tooltip formatter={(v) => fmtN(Number(v ?? 0))} />
                                        <Bar dataKey="clicks" name="Cliques" radius={[2,2,0,0]}>
                                            {Array.from({ length: 24 }, (_, h) => {
                                                const found = detail!.hourly_clicks.find(d => d.hour === h);
                                                const v = found?.clicks ?? 0;
                                                const max = Math.max(...detail!.hourly_clicks.map(d => d.clicks), 1);
                                                const intensity = v / max;
                                                const color = intensity > 0.6 ? '#4f46e5' : intensity > 0.3 ? '#818cf8' : intensity > 0 ? '#c7d2fe' : '#e5e7eb';
                                                return <Cell key={h} fill={color} />;
                                            })}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        )}
                    </section>

                    {/* Seção 4 — Regiões */}
                    <section>
                        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                            Regiões — cliques por timezone
                        </h3>
                        {detailQuery.isLoading ? (
                            <div className="space-y-2">
                                {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-8" />)}
                            </div>
                        ) : topCountries.length === 0 ? (
                            <p className="text-sm text-gray-400">Sem dados de região.</p>
                        ) : (
                            <div className="rounded-xl border border-gray-100 p-4">
                                <ResponsiveContainer width="100%" height={Math.max(topCountries.length * 36, 200)}>
                                    <BarChart
                                        layout="vertical"
                                        data={topCountries.map(row => ({
                                            name: TZ_LABEL[row.country] ?? row.country,
                                            cliques: row.clicks,
                                            leads: row.unique_leads,
                                        }))}
                                        margin={{ top: 4, right: 60, left: 8, bottom: 4 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                                        <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={v => fmtN(v as number)} />
                                        <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={96} />
                                        <Tooltip formatter={(v, name) => [fmtN(Number(v ?? 0)), name === 'cliques' ? 'Cliques' : 'Clicadores únicos']} />
                                        <Legend />
                                        <Bar dataKey="cliques" name="Cliques" fill="#6366f1" radius={[0,3,3,0]} />
                                        <Bar dataKey="leads" name="Clicadores únicos" fill="#10b981" radius={[0,3,3,0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        )}
                    </section>

                    {/* Seção 4 — Revenue por dia */}
                    {(revenueLoading || chartData.length > 0) && (
                        <section>
                            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                                Revenue por dia
                            </h3>
                            {revenueLoading ? (
                                <Skeleton className="h-48" />
                            ) : (
                                <div className="rounded-xl border border-gray-100 p-4">
                                    <ResponsiveContainer width="100%" height={200}>
                                        <LineChart data={chartData} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                            <XAxis dataKey="date_fmt" tick={{ fontSize: 11 }} />
                                            <YAxis
                                                tick={{ fontSize: 11 }}
                                                width={64}
                                                tickFormatter={v => `$${fmtN(v as number)}`}
                                            />
                                            <Tooltip content={<RevenueTooltip />} />
                                            <Legend />
                                            {chartDomains.map((d, i) => (
                                                <Line
                                                    key={d}
                                                    type="monotone"
                                                    dataKey={d}
                                                    name={d}
                                                    stroke={DOMAIN_COLORS[i % DOMAIN_COLORS.length]}
                                                    strokeWidth={2}
                                                    dot={{ r: 2 }}
                                                    activeDot={{ r: 4 }}
                                                />
                                            ))}
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                        </section>
                    )}
                </div>
            </div>
        </div>
    );
}
